/**
 * Hex Grid Utilities — Pure coordinate and geometry functions.
 *
 * Pointy-top hex orientation with odd-row offset storage.
 * Offset coords [col, row] are used for JSON storage and human editing.
 * Axial coords {q, r} are used internally for distance and neighbour math.
 *
 * hexSize: outer radius in metres (centre to vertex).
 *   - Col step = √3 × hexSize metres
 *   - Row step = 1.5 × hexSize metres
 *
 * See docs/golf/hex_grid_system.md for full design rationale.
 */

const SQRT3 = Math.sqrt(3);

// ── Coordinate conversions ─────────────────────────────────────────────────

/**
 * Convert offset [col, row] to axial {q, r}.
 * Odd-row offset: odd rows are shifted right by +0.5 col.
 */
export function offsetToAxial(col, row) {
  const q = col - (row - (row & 1)) / 2;
  const r = row;
  return { q, r };
}

/**
 * Convert axial {q, r} to offset [col, row].
 */
export function axialToOffset(q, r) {
  const col = q + (r - (r & 1)) / 2;
  const row = r;
  return { col, row };
}

/**
 * Round fractional axial coordinates to the nearest hex.
 * Uses cube coordinate rounding (q + r + s = 0).
 * Returns [q, r] of the nearest hex.
 */
function cubeRound(q, r) {
  const s = -q - r;
  let rq = Math.round(q);
  let rr = Math.round(r);
  let rs = Math.round(s);

  const dq = Math.abs(rq - q);
  const dr = Math.abs(rr - r);
  const ds = Math.abs(rs - s);

  if (dq > dr && dq > ds) {
    rq = -rr - rs;
  } else if (dr > ds) {
    rr = -rq - rs;
  }
  // rs is discarded — only q and r needed for axial

  return [rq, rr];
}

// ── Pixel geometry ─────────────────────────────────────────────────────────

/**
 * Pixel centre of a hex (pointy-top, odd-row right offset).
 * @param {number} col
 * @param {number} row
 * @param {number} pixelSize - outer radius in pixels
 * @returns {{ x: number, y: number }}
 */
export function hexCentrePx(col, row, pixelSize) {
  const x = pixelSize * SQRT3 * (col + (row & 1) * 0.5);
  const y = pixelSize * 1.5 * row;
  return { x, y };
}

/**
 * Six corner points of a hex (pointy-top, starting at -30°).
 * @param {number} cx - centre x in pixels
 * @param {number} cy - centre y in pixels
 * @param {number} pixelSize - outer radius in pixels
 * @returns {Array<{ x: number, y: number }>}
 */
export function hexCornersPx(cx, cy, pixelSize) {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 180) * (60 * i - 30);
    return {
      x: cx + pixelSize * Math.cos(angle),
      y: cy + pixelSize * Math.sin(angle),
    };
  });
}

/**
 * SVG path 'd' string for a hex polygon.
 * @param {number} cx
 * @param {number} cy
 * @param {number} pixelSize
 * @returns {string}
 */
export function hexPathString(cx, cy, pixelSize) {
  const corners = hexCornersPx(cx, cy, pixelSize);
  return corners.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ') + ' Z';
}

/**
 * Polygon points string for a hex (for use in SVG <polygon points="...">).
 * @param {number} cx
 * @param {number} cy
 * @param {number} pixelSize
 * @returns {string}
 */
export function hexPointsString(cx, cy, pixelSize) {
  return hexCornersPx(cx, cy, pixelSize)
    .map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' ');
}

// ── Metre-space geometry ───────────────────────────────────────────────────

/**
 * Metre-space centre of a hex.
 * @param {number} col
 * @param {number} row
 * @param {number} hexSize - outer radius in metres
 * @returns {{ x: number, y: number }}
 */
export function hexCentreMetres(col, row, hexSize) {
  const x = hexSize * SQRT3 * (col + (row & 1) * 0.5);
  const y = hexSize * 1.5 * row;
  return { x, y };
}

/**
 * Find the nearest hex for a given metre position.
 * Converts to fractional axial, rounds via cube rounding.
 * @param {number} mx - x in metres
 * @param {number} my - y in metres
 * @param {number} hexSize - outer radius in metres
 * @returns {{ col: number, row: number }}
 */
export function metreToHex(mx, my, hexSize) {
  const q = ((SQRT3 / 3) * mx - (1 / 3) * my) / hexSize;
  const r = ((2 / 3) * my) / hexSize;
  const [rq, rr] = cubeRound(q, r);
  return axialToOffset(rq, rr);
}

// ── Map utilities ──────────────────────────────────────────────────────────

/**
 * Build a Map from "col,row" key → terrain string.
 * @param {Array<{ col: number, row: number, terrain: string }>} hexes
 * @returns {Map<string, string>}
 */
export function buildHexMap(hexes) {
  const map = new Map();
  for (const { col, row, terrain } of hexes) {
    map.set(`${col},${row}`, terrain);
  }
  return map;
}

/**
 * Return the 6 offset-coord neighbours of a hex (pointy-top, odd-row offset).
 * @param {number} col
 * @param {number} row
 * @returns {Array<{ col: number, row: number }>}
 */
export function hexNeighbours(col, row) {
  const isOdd = row & 1;
  const dirs = isOdd
    ? [
        [+1, 0], [-1, 0],
        [0, -1], [+1, -1],
        [0, +1], [+1, +1],
      ]
    : [
        [+1, 0], [-1, 0],
        [-1, -1], [0, -1],
        [-1, +1], [0, +1],
      ];
  return dirs.map(([dc, dr]) => ({ col: col + dc, row: row + dr }));
}

/**
 * Hex distance in steps between two offset-coord hexes.
 * Converts to axial, then uses cube distance.
 */
export function hexDistance(col1, row1, col2, row2) {
  const a1 = offsetToAxial(col1, row1);
  const a2 = offsetToAxial(col2, row2);
  const dq = a1.q - a2.q;
  const dr = a1.r - a2.r;
  const ds = -dq - dr;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(ds));
}

/**
 * Bounding box in metres covering all hexes (including their full extent).
 * Returns { minX, minY, maxX, maxY, width, height }.
 * @param {Array<{ col: number, row: number }>} hexes
 * @param {number} hexSize - outer radius in metres
 */
export function hexBounds(hexes, hexSize) {
  if (!hexes || hexes.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  const halfW = (SQRT3 / 2) * hexSize; // half flat-to-flat width
  const halfH = hexSize;                // half vertex-to-vertex height

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const { col, row } of hexes) {
    const { x, y } = hexCentreMetres(col, row, hexSize);
    if (x - halfW < minX) minX = x - halfW;
    if (y - halfH < minY) minY = y - halfH;
    if (x + halfW > maxX) maxX = x + halfW;
    if (y + halfH > maxY) maxY = y + halfH;
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
